import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpHuddingePage: ServicePageConfig = {
  slug: "flytthjalp-huddinge",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Huddinge – Skåpbil och bärare | Ungdomskraft",
    description:
      "Boka flytthjälp i Huddinge: Flemingsberg, Stuvsta, Segeltorp, Skogås, Trångsund och Vårby. Skåpbil och bärare, fast paketpris och RUT-avdrag.",
    h1: "Flytthjälp Huddinge",
  },

  hero: {
    heading: "Flytthjälp i Huddinge",
    intro:
      "Från studentlägenheten i Flemingsberg till radhuset i Vistaberg eller villan i Stuvsta – vi flyttar dig i hela Huddinge. Två eller fler bärare kommer med skåpbil, bär ut, kör och bär in. Du väljer paket och ser priset innan du bokar.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft lastar möbler vid flytthjälp i Huddinge",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har flyttat hushåll i Stockholms län sedan 2014. I Huddinge möter vi allt från studentkorridorer vid Södertörns högskola till villor med källare i Snättringe, och våra bärare vet hur man lastar för båda.",
    bookYourText: "Boka din Huddingeflytt",
    bookDescText:
      "Fyll i adressen du lämnar och den nya adressen, välj paket och en ledig tid. Priset syns direkt.",
    vehicleTitleText: "Ingen hyrbil behövs",
    vehicleDescText:
      "Skåpbil, filtar och spännband ingår. Vi lastar, kör och lossar – du behöver inte köra själv på E4 eller Huddingevägen.",
  },

  itemsSection: {
    heading: "Vanliga saker vi flyttar i Huddinge",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Bäddsoffor, hörnsoffor och fåtöljer",
        image: "/sofa.webp",
        alt: "Bäddsoffa som bärs vid studentflytt i Flemingsberg, Huddinge",
      },
      {
        name: "Sängar & madrasser",
        description: "Enkelsängar, dubbelsängar och kontinentalsängar",
        image: "/bed.webp",
        alt: "Säng som flyttas från radhus i Vistaberg, Huddinge",
      },
      {
        name: "Skrivbord & matbord",
        description: "Skrivbord, matbord och stolar – isärtagna när det behövs",
        image: "/table.webp",
        alt: "Skrivbord som lastas vid flytt i Huddinge centrum",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, torktumlare, kylskåp och frys",
        image: "/washer.webp",
        alt: "Kylskåp som flyttas från villa i Stuvsta, Huddinge",
      },
      {
        name: "Garderober & hyllor",
        description: "Garderober, hyllsystem och byråer",
        image: "/wardrobe.webp",
        alt: "Garderob som bärs ut vid flytt i Skogås, Huddinge",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger med kurslitteratur, kök, kläder och förråd",
        image: "/box.webp",
        alt: "Flyttkartonger vid flytt i Segeltorp, Huddinge",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Huddinge – studentboenden, radhus och villor",
    mainText:
      "Huddinge är en stor kommun med många slags bostäder, och flytten planeras olika beroende på vart du ska. I Flemingsberg, med Karolinska Universitetssjukhuset i Huddinge och Södertörns högskola, finns många studentlägenheter och nya flerbostadshus – där är flyttarna ofta små och tidspressade vid terminsstart. Kring Huddinge centrum, Sjödalen och Fullersta bor många i lägenhet med hiss. I norr ligger villaområdena Stuvsta, Snättringe, Segeltorp och Glömsta, och i Vistaberg radhus. Skogås och Trångsund vid Drevviken har en blandning av flerbostadshus, radhus och villor, och Vårby gård och Masmo ligger längs tunnelbanans röda linje.\n\nEn vanlig flytt till Huddinge kommer från söderort eller innerstaden, ofta en familj som går från lägenhet till radhus eller villa. En annan är flytten åt andra hållet: studenten som lämnar korridoren i Flemingsberg efter examen. För den mindre flytten räcker ofta paketet Mellan eller Stor med två bärare och en skåpbil. För ett radhus eller en villa med källare och förråd behövs Extra Stor eller Mega Stor, så att det finns tid för allt som står i garaget.\n\nPendeltåget stannar i Stuvsta, Huddinge och Flemingsberg, och Nynäsbanan i Trångsund och Skogås. För oss som kör är E4/E20 förbi Kungens kurva och Vårby, Huddingevägen och Glömstavägen de viktigaste vägarna genom kommunen. I villaområdena backar vi helst in på uppfarten, och i de stora bostadsområdena frågar vi i förväg efter lastplats, så att bärarna inte behöver gå långt med tunga möbler.",
    preparationHeading: "Förbered flytten i Huddinge",
    preparationText:
      "Packa kartonger rum för rum och märk dem tydligt. Töm byråer och garderober, frosta av frysen och koppla ur det som går att koppla ur. Ska du flytta från ett studentrum i Flemingsberg, kolla vad som gäller för utflyttning och nyckelåterlämning hos din hyresvärd, så att flytten och besiktningen hinns med samma dag.\n\nI villaområden som Snättringe och Stuvsta: se till att uppfarten är fri så att vi kan backa nära. I lägenhetsområden, ta reda på om det finns en lastzon eller om bilen får köra in på gården. Tänk på förrådet i källaren eller på vinden – det glöms ofta bort tills sist. Portkoder och nycklar för båda adresserna gör att vi kan börja direkt.",
    additionalSections: [
      {
        heading: "Kommundelar vi flyttar i, i Huddinge",
        content:
          "Vi flyttar i hela Huddinge kommun: Huddinge centrum, Sjödalen, Fullersta, Flemingsberg, Visättra, Stuvsta, Snättringe, Segeltorp, Glömsta, Vistaberg, Skogås, Trångsund, Vårby gård och Masmo.\n\nFlyttar du mellan Huddinge och en annan kommun går det lika bra – till exempel till Stockholm, Botkyrka, Haninge eller Tyresö. Körsträcka inom 20 km ingår i paketpriset och längre sträckor räknas per kilometer. Totalen syns när du fyllt i adresserna.",
      },
      {
        heading: "Studentflytt i Flemingsberg",
        content:
          "Runt Södertörns högskola och KI:s campus i Flemingsberg flyttar många studenter in och ut vid terminsskiften. En studentflytt är oftast en säng, ett skrivbord, några hyllor och ett tiotal kartonger – det ryms i paketet Mellan eller Stor. Behöver du bara hjälp att bära, utan bil, finns bärhjälp som egen tjänst.",
        cta: { text: "Se bärhjälp vid flytt →", href: "/barhjalp-flytt" },
      },
      {
        heading: "Varför boka flytthjälp i Huddinge med Ungdomskraft?",
        listItems: [
          {
            label: "Från korridor till villa –",
            text: "Vi anpassar paket och antal bärare efter om du flyttar ett studentrum i Flemingsberg eller en villa i Segeltorp.",
          },
          {
            label: "Fast pris –",
            text: "Paketpriset och körsträckan syns innan du bokar. Du slipper timdebitering.",
          },
          {
            label: "RUT-avdrag –",
            text: "Halva arbetskostnaden dras av direkt på fakturan, upp till 75 000 kr per person och år.",
          },
          {
            label: "Försäkring –",
            text: "Uppdragen är försäkrade genom Trygg Hansa.",
          },
          {
            label: "Alla dagar –",
            text: "Vi flyttar även på helger, vilket passar bra vid terminsstart och månadsskiften.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Huddinge?",
      answer:
        "Paketen går från Liten (995 kr, 1–2 föremål) via Mellan (1 495 kr, 3–6) och Stor (2 495 kr, 7–10) till Extra Stor (4 995 kr, upp till 4 h) och Mega Stor (8 995 kr, upp till 8 h). Körsträcka inom 20 km ingår. Priset visas direkt när du bokar.",
    },
    {
      question: "Vilket paket passar en studentflytt i Flemingsberg?",
      answer:
        "Ett studentrum med säng, skrivbord, hyllor och kartonger brukar rymmas i Mellan eller Stor. Är du osäker, välj det större så att allt hinns med.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp i Huddinge?",
      answer:
        "Ja, 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det på fakturan.",
    },
    {
      question: "Flyttar ni villor och radhus i Stuvsta, Snättringe och Vistaberg?",
      answer:
        "Ja. Villor och radhus har ofta källare, garage och förråd, så välj Extra Stor eller Mega Stor för att få tid till allt.",
    },
    {
      question: "Hur snabbt kan ni flytta mig i Huddinge?",
      answer:
        "Ofta inom några dagar. Du ser lediga tider i bokningen när du skrivit in adresserna.",
    },
    {
      question: "Ingår skåpbil?",
      answer:
        "Ja, alla paket från Liten och uppåt har skåpbil och två personer.",
    },
    {
      question: "Bär ni i trappor utan hiss?",
      answer:
        "Ja. Ange våning och om det finns hiss, så räknar vi med det.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, tvättmaskin, torktumlare, kyl och frys. Frosta av och töm kyl och frys dagen innan.",
    },
    {
      question: "Monterar ni möbler?",
      answer:
        "Vi tar isär och sätter ihop möbler som behöver det för att komma igenom dörrar och trappor. Spara skruvarna i en påse.",
    },
    {
      question: "Kan jag flytta på helgen?",
      answer:
        "Ja, vi flyttar alla dagar, även lördag och söndag.",
    },
    {
      question: "Kan ni flytta från Huddinge till en annan kommun?",
      answer:
        "Ja. Körsträcka inom 20 km ingår, och längre sträckor debiteras per kilometer enligt paketet. Du ser totalen innan du bokar.",
    },
    {
      question: "Skyddar ni möbler och trapphus?",
      answer:
        "Ja, med möbelfiltar och bärremmar. Uppdraget är försäkrat genom Trygg Hansa.",
    },
    {
      question: "Vilka delar av Huddinge täcker ni?",
      answer:
        "Hela kommunen: Huddinge centrum, Sjödalen, Fullersta, Flemingsberg, Visättra, Stuvsta, Snättringe, Segeltorp, Glömsta, Vistaberg, Skogås, Trångsund, Vårby gård och Masmo.",
    },
    {
      question: "Vilka andra kommuner i länet flyttar ni i?",
      answer: `Hela Stockholmsregionen: ${stockholmKommunerText}. Samma prismodell överallt – paketpris plus körsträcka över 20 km.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Huddinge i 3 steg",
    step2Text:
      "Bärarna kommer med skåpbil till din adress i Huddinge, bär ut allt och lastar säkert. Sedan kör vi raka vägen till nya hemmet.",
    step3Title: "Framme och inburet",
    step3Desc:
      "Vi bär in allt i rätt rum och sätter ihop det vi tog isär. Sedan är det bara att packa upp.",
    ctaHeading: "Boka flytthjälp i Huddinge",
    ctaText:
      "Skriv in adresserna, välj paket och tid – priset syns direkt, med bil och bärare.",
  },

  en: {
    slug: "moving-help-huddinge",
    meta: {
      title: "Moving Help Huddinge – Van & Crew | Ungdomskraft",
      description:
        "Book moving help in Huddinge: Flemingsberg, Stuvsta, Segeltorp, Skogås, Trångsund and Vårby. Van and carriers, fixed package price and RUT deduction.",
      h1: "Moving Help Huddinge",
    },
    hero: {
      heading: "Moving Help in Huddinge",
      intro:
        "From the student flat in Flemingsberg to the terraced house in Vistaberg or the villa in Stuvsta – we move you anywhere in Huddinge. Two or more carriers arrive with a van, carry out, drive and carry in. You choose a package and see the price before you book.",
      heroImageAlt: "Ungdomskraft loading furniture during a move in Huddinge",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has moved households in Stockholm County since 2014. In Huddinge we meet everything from student corridors at Södertörn University to villas with basements in Snättringe, and our carriers know how to load for both.",
      bookYourText: "Book your Huddinge move",
      bookDescText:
        "Enter the address you are leaving and the new one, choose a package and a free time. The price shows straight away.",
      vehicleTitleText: "No rental van needed",
      vehicleDescText:
        "Van, blankets and straps are included. We load, drive and unload – you don't have to drive the E4 or Huddingevägen yourself.",
    },
    itemsSection: {
      heading: "Common items we move in Huddinge",
      items: [
        { name: "Sofas & armchairs", description: "Sofa beds, corner sofas and armchairs", image: "/sofa.webp", alt: "Sofa bed carried during a student move in Flemingsberg, Huddinge" },
        { name: "Beds & mattresses", description: "Single, double and continental beds", image: "/bed.webp", alt: "Bed moved from a terraced house in Vistaberg, Huddinge" },
        { name: "Desks & dining tables", description: "Desks, dining tables and chairs – taken apart when needed", image: "/table.webp", alt: "Desk loaded during a move in Huddinge centrum" },
        { name: "Appliances", description: "Washing machine, tumble dryer, fridge and freezer", image: "/washer.webp", alt: "Fridge moved from a villa in Stuvsta, Huddinge" },
        { name: "Wardrobes & shelves", description: "Wardrobes, shelving systems and dressers", image: "/wardrobe.webp", alt: "Wardrobe carried out during a move in Skogås, Huddinge" },
        { name: "Moving boxes", description: "Boxes with textbooks, kitchenware, clothes and storage items", image: "/box.webp", alt: "Moving boxes during a move in Segeltorp, Huddinge" },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Huddinge – student housing, terraced houses and villas",
      mainText:
        "Huddinge is a large municipality with many kinds of homes, and a move is planned differently depending on where you are going. Flemingsberg, home to Karolinska University Hospital Huddinge and Södertörn University, has lots of student flats and new apartment blocks – moves there are often small and squeezed around the start of term. Around Huddinge centrum, Sjödalen and Fullersta many people live in flats with lifts. In the north are the villa areas of Stuvsta, Snättringe, Segeltorp and Glömsta, and Vistaberg has terraced houses. Skogås and Trångsund by Lake Drevviken mix apartment blocks, terraced houses and villas, and Vårby gård and Masmo lie along the red metro line.\n\nA common move to Huddinge comes from Stockholm's southern suburbs or the inner city, often a family going from a flat to a terraced house or villa. Another goes the other way: the student leaving the corridor in Flemingsberg after graduating. For the smaller move, the Medium or Large package with two carriers and a van is often enough. A terraced house or villa with a basement and storage needs Extra Large or Mega Large, so there is time for everything in the garage.\n\nThe commuter train stops at Stuvsta, Huddinge and Flemingsberg, and the Nynäs line at Trångsund and Skogås. For us on the road, the E4/E20 past Kungens kurva and Vårby, Huddingevägen and Glömstavägen are the key routes through the municipality. In villa areas we prefer to reverse onto the driveway, and in the large residential areas we ask about a loading spot in advance, so the carriers don't have to walk far with heavy furniture.",
      preparationHeading: "Prepare your move in Huddinge",
      preparationText:
        "Pack boxes room by room and label them clearly. Empty dressers and wardrobes, defrost the freezer and unplug what can be unplugged. If you are leaving a student room in Flemingsberg, check your landlord's rules for moving out and returning keys, so the move and the inspection fit on the same day.\n\nIn villa areas such as Snättringe and Stuvsta, keep the driveway clear so we can reverse close. In apartment areas, find out whether there is a loading zone or whether the van may drive into the courtyard. Remember the storage room in the basement or attic – it is often forgotten until last. Door codes and keys for both addresses let us start straight away.",
      additionalSections: [
        {
          heading: "Districts we move in, in Huddinge",
          content:
            "We move across the whole of Huddinge Municipality: Huddinge centrum, Sjödalen, Fullersta, Flemingsberg, Visättra, Stuvsta, Snättringe, Segeltorp, Glömsta, Vistaberg, Skogås, Trångsund, Vårby gård and Masmo.\n\nMoving between Huddinge and another municipality works just as well – for example to Stockholm, Botkyrka, Haninge or Tyresö. Driving within 20 km is included in the package price and longer distances are charged per kilometre. The total shows once you have entered the addresses.",
        },
        {
          heading: "Student moves in Flemingsberg",
          content:
            "Around Södertörn University and the KI campus in Flemingsberg, many students move in and out at the turn of term. A student move is usually a bed, a desk, a few shelves and around ten boxes – that fits in the Medium or Large package. If you only need carrying help, without a van, that is available as a separate service.",
          cta: { text: "Carrying help for moving →", href: "/en/carrying-help-move" },
        },
        {
          heading: "Why book moving help in Huddinge with Ungdomskraft?",
          listItems: [
            { label: "From corridor to villa –", text: "We adjust package and number of carriers to whether you are moving a student room in Flemingsberg or a villa in Segeltorp." },
            { label: "Fixed price –", text: "The package price and driving distance show before you book. No hourly billing." },
            { label: "RUT deduction –", text: "Half the labour cost is deducted directly on the invoice, up to SEK 75,000 per person per year." },
            { label: "Insurance –", text: "Jobs are insured through Trygg Hansa." },
            { label: "Every day –", text: "We move at weekends too, which suits the start of term and the turn of the month." },
          ],
        },
      ],
    },
    faq: [
      { question: "How much does moving help in Huddinge cost?", answer: "Packages run from Small (SEK 995, 1–2 items) via Medium (SEK 1,495, 3–6) and Large (SEK 2,495, 7–10) to Extra Large (SEK 4,995, up to 4 h) and Mega Large (SEK 8,995, up to 8 h). Driving within 20 km is included. The price is shown when you book." },
      { question: "Which package suits a student move in Flemingsberg?", answer: "A student room with bed, desk, shelves and boxes usually fits in Medium or Large. If unsure, pick the larger one so everything gets done." },
      { question: "Can I get a RUT deduction on moving help in Huddinge?", answer: "Yes, 50% of the labour cost, up to SEK 75,000 per person per year. We deduct it on the invoice." },
      { question: "Do you move villas and terraced houses in Stuvsta, Snättringe and Vistaberg?", answer: "Yes. Villas and terraced houses often have basements, garages and storage, so choose Extra Large or Mega Large to have time for everything." },
      { question: "How soon can you move me in Huddinge?", answer: "Often within a few days. You see free times in the booking once you have entered the addresses." },
      { question: "Is a van included?", answer: "Yes, every package from Small upwards has a van and two people." },
      { question: "Do you carry on stairs without a lift?", answer: "Yes. State the floor and whether there is a lift, and we plan for it." },
      { question: "Do you move appliances?", answer: "Yes – washing machine, tumble dryer, fridge and freezer. Defrost and empty the fridge and freezer the day before." },
      { question: "Do you assemble furniture?", answer: "We take apart and put back together furniture that needs it to get through doors and stairwells. Keep the screws in a bag." },
      { question: "Can I move at the weekend?", answer: "Yes, we move every day, including Saturday and Sunday." },
      { question: "Can you move me from Huddinge to another municipality?", answer: "Yes. Driving within 20 km is included, and longer distances are charged per kilometre according to the package. You see the total before you book." },
      { question: "Do you protect furniture and stairwells?", answer: "Yes, with furniture blankets and carrying straps. The job is insured through Trygg Hansa." },
      { question: "Which parts of Huddinge do you cover?", answer: "The whole municipality: Huddinge centrum, Sjödalen, Fullersta, Flemingsberg, Visättra, Stuvsta, Snättringe, Segeltorp, Glömsta, Vistaberg, Skogås, Trångsund, Vårby gård and Masmo." },
      { question: "Which other municipalities in the county do you serve?", answer: `The whole Stockholm region: ${stockholmKommunerText}. Same pricing everywhere – package price plus driving beyond 20 km.` },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Huddinge in 3 steps",
      step2Text:
        "The carriers arrive with a van at your Huddinge address, carry everything out and load it securely. Then we drive straight to your new home.",
      step3Title: "Arrived and carried in",
      step3Desc:
        "We carry everything into the right rooms and reassemble what we took apart. Then all that's left is unpacking.",
      ctaHeading: "Book moving help in Huddinge",
      ctaText:
        "Enter the addresses, choose a package and time – the price shows straight away, with van and carriers.",
    },
  },
};
